import { useEffect, useMemo, useRef, useState } from 'react'
import { useReducedMotion } from '@/hooks/use-reduced-motion'

/**
 * Smoothly animates every value of a numeric array towards a new target.
 * Arrays are compared by value, so passing a fresh array each render is safe.
 */
export const useTweenedArray = (input: Array<number>, duration = 650): Array<number> => {
    const reduced = useReducedMotion()
    const key = input.map(value => value.toFixed(3)).join('|')
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const target = useMemo(() => input, [key])
    const [values, setValues] = useState(target)
    const current = useRef(target)

    useEffect(() => {
        const from = current.current

        if (reduced || from.length !== target.length) {
            current.current = target
            setValues(target)
            return
        }

        let frame = 0
        let startedAt: number | null = null

        const step = (now: number) => {
            if (startedAt === null) startedAt = now

            const progress = Math.min(1, (now - startedAt) / duration)
            const eased = 1 - Math.pow(1 - progress, 3)
            const next = target.map((value, index) => from[index] + (value - from[index]) * eased)

            current.current = next
            setValues(next)

            if (progress < 1) frame = requestAnimationFrame(step)
        }

        frame = requestAnimationFrame(step)

        return () => cancelAnimationFrame(frame)
    }, [target, duration, reduced])

    return values
}
